import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Button, Dropdown, Menu, MenuProps, notification, Tooltip } from "antd";
import { ProfileOutlined } from "@ant-design/icons";
import { PAGINATION } from "src/constants/common";
import RequestAppAction from "src/store/slices/appActions";
import { getCurrentDidId } from "src/store/selectors/features/currentDid";
import { getTemplateList, getTemplatePagination, getTemplateResetStatus } from "src/store/selectors/features/templateList";
import { setTemplateIsReset, setTemplateList, setTemplatePagination } from "src/store/slices/features/templateList";

interface ITemplateBtn {
  disabled?: boolean;
  handleMenuItemClick?: (item: any) => void;
  btnClass?: string;
  style?: any;
  hasAddNewTemplateBtn?: boolean;
}

const TemplateBtn = ({
  disabled = false,
  handleMenuItemClick,
  btnClass = "",
  style = { borderRadius: 50, marginLeft: 10 },
  hasAddNewTemplateBtn,
}: ITemplateBtn) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const did = useSelector(getCurrentDidId);
  const templateList: any = useSelector(getTemplateList);
  const templatePagination: any = useSelector(getTemplatePagination);
  const templateIsReset: any = useSelector(getTemplateResetStatus);

  const nextPageRef = useRef(PAGINATION.page);
  const scrollFetchRef = useRef(false);

  const [isFinalFetch, setIsFinalFetch] = useState(false);

  const updateTemplateList = (data: any) => {
    if (!data) {
      return;
    }

    let newTemplateList: any[] = Array.isArray(templateList) ? [...templateList] : [];

    if (newTemplateList?.length > 0) {
      if (data?.items?.length > 0) {
        data.items.forEach((item: any) => {
          const findTemplate = newTemplateList.find((i: any) => i?.id === item?.id);

          if (!findTemplate) {
            newTemplateList.push(item);
          }
        });
      }
    } else {
      newTemplateList = [...data?.items];
    }

    dispatch(setTemplateList(newTemplateList));
    dispatch(setTemplatePagination([
      ...(Array.isArray(templatePagination) ? templatePagination : []),
      data,
    ]));
  };

  const fetchTemplate = () => {
    if (!did || scrollFetchRef.current) {
      return;
    }

    scrollFetchRef.current = true;

    const pageToFetch = nextPageRef.current ?? PAGINATION.page;

    const fetchPagination = {
      page: pageToFetch,
      limit: PAGINATION.limit,
    };

    dispatch(
      RequestAppAction.fetchTemplate({
        id: did,
        meta: fetchPagination,
        cbSuccess: (response: any) => {
          const data = response?.data;

          if (data) {
            // First page data is already updated on fetchTemplate
            if (pageToFetch > 1) {
              updateTemplateList(data);
            }

            nextPageRef.current = pageToFetch + 1;

            if (!data?.items || data?.items?.length === 0) {
              setIsFinalFetch(true);
            }
          }

          scrollFetchRef.current = false;
        },
        cbFailure: (e: string) => {
          notification.error({
            message: e ?? t("notification.getTemplateFail")
          });
          scrollFetchRef.current = false;
        },
      })
    );
  };

  useEffect(() => {
    if (!did) {
      resetStates();
      return;
    }

    setIsFinalFetch(false);

    if (!templateList || templateList?.length === 0) {
      fetchTemplate();
    } else {
      nextPageRef.current = nextPageRef.current + 1;
    }
  }, [did]);

  useEffect(() => {
    if (templateIsReset) {
      resetStates();
      dispatch(setTemplateIsReset(false));
    }
  }, [templateIsReset]);

  const resetStates = () => {
    setIsFinalFetch(false);
    nextPageRef.current = PAGINATION.page;
  };

  const menuItems: MenuProps["items"] =
    Array.isArray(templateList) && templateList.length > 0
      ? templateList.map(
        (template: { content: any; title: string }, index: any) => ({
          key: String(index),
          label: template.title,
          content: template?.content,
        })
      ) : [];

  if (hasAddNewTemplateBtn) {
    menuItems?.push({ key: "Add New Template", label: "Add New Template" });
  }

  const menu = () => (
    <Menu
      style={{ maxHeight: 300, overflowY: "auto" }}
      items={menuItems && menuItems.length > 0 ? menuItems : []}
      onClick={(info) => {
        if (!handleMenuItemClick) return;

        const clickedItem = menuItems.find((i) => i?.key === info.key);
        if (!clickedItem) return;

        handleMenuItemClick(clickedItem);
      }}
      onScroll={onScroll}
    />
  );

  const onScroll = (e: any) => {
    if (!did || !e?.currentTarget || isFinalFetch) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 10;

    if (!isNearBottom) {
      return;
    }

    if (!Array.isArray(templatePagination) || templatePagination.length === 0) {
      fetchTemplate();
      return;
    }

    const lastMeta = templatePagination[templatePagination.length - 1]?.meta;

    if (!lastMeta) {
      fetchTemplate();
      return;
    }

    const nextPage = nextPageRef.current;

    if (lastMeta.totalPages < nextPage) {
      setIsFinalFetch(true);
      return;
    }

    if (nextPage > lastMeta.page) {
      fetchTemplate();
    } else {
      nextPageRef.current = lastMeta.page + 1;
    }
  };

  return (
    (!menuItems || menuItems?.length === 0) ? (
      <Tooltip title={t("tootlTip.noTemplateAvailable")}>
        <Button
          disabled
          icon={<ProfileOutlined />}
          className={btnClass}
          style={style}
        />
      </Tooltip>
    ) : (
      <Dropdown
        dropdownRender={menu}
        placement="topRight"
        arrow
      >
        <Button
          disabled={disabled}
          icon={<ProfileOutlined />}
          className={btnClass}
          style={style}
        />
      </Dropdown>
    )
  );
};

export default TemplateBtn;
